import { VariantProperties, ConfiguratorPV, attributeValueCase, CUSTOM_ATTRIBUTE_VALUE, isSystemAttribute, createAttributeValue } from '@canvas-logic/clp-domain';
import { LogicEngine } from '@canvas-logic/clp-logic-engine';

function assertUnreachable(_value, message) {
    throw new Error(message);
}

class LogicEngineToConfiguratorValueMediator {
    constructor(customValues) {
        this.customValues = customValues;
    }
    packProductVariantsForLE(componentKey, variants) {
        return variants.map((variant) => this.packProductVariantForLE(componentKey, variant));
    }
    packProductVariantForLE(componentKey, productVariant) {
        if (productVariant === null) {
            return {
                [VariantProperties.available]: false,
            };
        }
        if (!productVariant) {
            throw new Error(`Empty product variant should be null`);
        }
        const variant = {};
        const configuratorVariant = new ConfiguratorPV(productVariant);
        for (const [attributeKey, value] of Object.entries(configuratorVariant.omitProperties())) {
            variant[attributeKey] = this.packAttributeValueForLE(componentKey, attributeKey, value);
        }
        for (const [attributeKey, value] of Object.entries(configuratorVariant.omitProductAttributes())) {
            variant[attributeKey] = value.value;
        }
        return variant;
    }
    packAttributeValueForLE(componentKey, attributeKey, value) {
        const attributeValue = attributeValueCase(value);
        const type = attributeValue.type;
        switch (type) {
            case 'value':
                return attributeValue.value;
            case 'emptyCustom':
                this.customValues.removeValue(componentKey, attributeKey);
                return CUSTOM_ATTRIBUTE_VALUE;
            case 'customValue':
                this.customValues.setValue(componentKey, attributeKey, attributeValue.value);
                return CUSTOM_ATTRIBUTE_VALUE;
            default:
                assertUnreachable(type, 'Invalid attribute value type');
        }
    }
    unpackAttributeValues(componentKey, attributeKey, attributeValues, skipCustomValue) {
        return attributeValues.map((attributeValue) => this.unpackAttributeValue(componentKey, attributeKey, attributeValue, skipCustomValue));
    }
    unpackAttributeValue(componentKey, attributeKey, attributeValue, skipCustomValue) {
        if (isSystemAttribute(attributeKey)) {
            return {
                value: attributeValue,
            };
        }
        if (attributeValue === CUSTOM_ATTRIBUTE_VALUE) {
            if (skipCustomValue) {
                return { custom: true };
            }
            const customAttributeValue = this.customValues.getValue(componentKey, attributeKey);
            if (!customAttributeValue) {
                return { custom: true };
            }
            else {
                return { custom: true, value: customAttributeValue };
            }
        }
        else {
            return { value: attributeValue };
        }
    }
    unpackCompositeProductVariants(composite) {
        if (!composite) {
            throw new Error('Logic engine found no compatible variant for the requested values.');
        }
        const result = {};
        for (const [componentKey, value] of Object.entries(composite)) {
            result[componentKey] = this.unpackProductVariant(componentKey, value);
        }
        return result;
    }
    unpackProductVariant(componentKey, product) {
        const result = {};
        for (const [attributeKey, value] of Object.entries(product)) {
            result[attributeKey] = this.unpackAttributeValue(componentKey, attributeKey, value, false);
        }
        return result;
    }
    packCompositeProductForLE(composite) {
        const result = {};
        for (const [componentKey, variant] of Object.entries(composite)) {
            result[componentKey] = this.packProductVariantForLE(componentKey, variant);
        }
        return result;
    }
}

class LogicEngineAdapter {
    get explain() {
        return this.logicEngine.explain;
    }
    constructor(logicEngine, valueMediator) {
        this.logicEngine = logicEngine;
        this.valueMediator = valueMediator;
    }
    getCompatibleValues(product, componentKey, attributeKey, validationOptions) {
        const mappedProduct = this.valueMediator.packCompositeProductForLE(product);
        const values = this.logicEngine.getCompatibleValues(mappedProduct, componentKey, attributeKey, validationOptions);
        return this.valueMediator.unpackAttributeValues(componentKey, attributeKey, values, true);
    }
    setValue(product, componentKey, attributeKey, attributeValue, validationOptions) {
        const result = this.logicEngine.setValue(this.valueMediator.packCompositeProductForLE(product), componentKey, attributeKey, this.valueMediator.packAttributeValueForLE(componentKey, attributeKey, attributeValue), validationOptions);
        return this.valueMediator.unpackCompositeProductVariants(result);
    }
    isValid(_product, _validationOptions) {
        throw new Error('Method not implemented.');
    }
    validate(product, validationOptions) {
        return this.logicEngine.validate(this.valueMediator.packCompositeProductForLE(product), validationOptions);
    }
    getFirstCompatibleVariantIgnoringRules() {
        return this.valueMediator.unpackCompositeProductVariants(this.logicEngine.getFirstCompatibleVariantIgnoringRules());
    }
    getAllValues(componentKey, attributeKey) {
        const result = this.logicEngine.getAllValues(componentKey, attributeKey);
        return this.valueMediator.unpackAttributeValues(componentKey, attributeKey, Array.from(result), true);
    }
}

class CustomValueHandler {
    constructor() {
        this.customs = {};
    }
    getValue(componentKey, attributeKey) {
        var _a;
        return (_a = this.customs[componentKey]) === null || _a === void 0 ? void 0 : _a[attributeKey];
    }
    removeValue(componentKey, attributeKey) {
        const component = this.customs[componentKey];
        if (!component) {
            return;
        }
        delete component[attributeKey];
    }
    setValue(componentKey, attributeKey, value) {
        const component = this.customs[componentKey];
        if (!component) {
            this.customs[componentKey] = {
                [attributeKey]: value,
            };
        }
        else {
            component[attributeKey] = value;
        }
    }
}

class LogicEngineBuilder {
    constructor() {
        this.variants = new Map();
        this.schema = { attributes: [] };
        this.rules = [];
        this.customValueHandler = new CustomValueHandler();
        this.attributeValueMapper = new LogicEngineToConfiguratorValueMediator(this.customValueHandler);
    }
    addProductVariants(componentKey, variants) {
        var _a;
        if ((_a = this.variants.get(componentKey)) === null || _a === void 0 ? void 0 : _a.length) {
            throw new Error('Product variants have already been added');
        }
        const mappedVariants = this.attributeValueMapper.packProductVariantsForLE(componentKey, variants);
        this.variants.set(componentKey, mappedVariants);
        return this;
    }
    setSchema(schema) {
        this.schema = schema;
        return this;
    }
    xsetRules(_rules) {
        return this;
    }
    setJsRules(rules) {
        this.rules = rules;
        return this;
    }
    xaddJsRule(_rule) {
        return this;
    }
    addJsRule(rule) {
        this.rules.push(rule);
        return this;
    }
    addStringRule(rule) {
        const jsRule = this.buildRule(rule);
        this.rules.push(jsRule);
        return this;
    }
    build() {
        const le = new LogicEngine(this.schema, this.rules, this.variants);
        return new LogicEngineAdapter(le, this.attributeValueMapper);
    }
    buildRule(rule) {
        return {
            name: rule.name,
            deps: rule.deps,
            fn: new Function('product', rule.fn),
        };
    }
    setStringRules(rules) {
        this.rules = [];
        rules.forEach((rule) => this.addStringRule(rule));
        return this;
    }
}

class ConfiguratorToDomainProductMapper {
    static toCfgCompositeVariants(variants) {
        const result = {};
        for (const [componentKey, componentVariants] of Object.entries(variants.components)) {
            const variants = [];
            for (const product of componentVariants) {
                if (product === null) {
                    const { mapped } = ConfiguratorToDomainProductMapper.toCfgEmptyComponentProductVariant(componentKey);
                    variants.push(mapped);
                    continue;
                }
                for (const productVariant of product.variants) {
                    if (productVariant === null) {
                        throw new Error('Product variant cannot be null');
                    }
                    const { mapped } = ConfiguratorToDomainProductMapper.toCfgProductVariant({
                        variant: productVariant,
                        componentKey,
                        product: product.product,
                        model3dId: productVariant.model3dId,
                    });
                    variants.push(mapped);
                }
            }
            result[componentKey] = variants;
        }
        return result;
    }
    static toCfgCompositeProduct(composite, customValues) {
        const result = {};
        for (const [componentKey, component] of Object.entries(composite.components)) {
            if (!component || !component.variant.productId) {
                const { mapped } = ConfiguratorToDomainProductMapper.toCfgEmptyComponentProductVariant(componentKey);
                result[componentKey] = mapped;
                continue;
            }
            const { mapped } = ConfiguratorToDomainProductMapper.toCfgProductVariant({
                variant: component.variant,
                product: component.product,
                componentKey: componentKey,
                model3dId: component.variant.model3dId,
            }, customValues === null || customValues === void 0 ? void 0 : customValues[componentKey]);
            result[componentKey] = mapped;
        }
        return result;
    }
    static toCfgProductVariant({ variant, product, componentKey, model3dId, }, customValues) {
        const pvc = ConfiguratorPV.fromRaw(variant.attributes, {
            available: true,
            variantId: String(variant.variantId),
            productId: String(product.productId),
            productKey: product.productKey,
            compoundId: ConfiguratorToDomainProductMapper.toVariantId(componentKey, variant.variantId),
            meta: variant.meta,
            model3dId: model3dId,
        }, customValues);
        return {
            mapped: pvc.raw,
            compoundId: pvc.compoundId,
        };
    }
    static toCfgProductVariantAttributes(attributes) {
        const result = {};
        for (const [key, value] of Object.entries(attributes)) {
            result[key] = createAttributeValue(value);
        }
        return result;
    }
    static toCfgProductVariantAttributesList(variantAttributes) {
        return variantAttributes.map((variant) => ConfiguratorToDomainProductMapper.toCfgProductVariantAttributes(variant));
    }
    static toCfgCompositeVariantAttributes(composite) {
        const result = {};
        for (const [componentKey, attributes] of Object.entries(composite)) {
            result[componentKey] = ConfiguratorToDomainProductMapper.toCfgProductVariantAttributes(attributes);
        }
        return result;
    }
    static toCfgEmptyComponentProductVariant(componentKey) {
        const compoundId = ConfiguratorToDomainProductMapper.toVariantId(componentKey, 'null');
        const mapped = ConfiguratorPV.fromEmpty({ compoundId }).raw;
        return {
            mapped,
            compoundId,
        };
    }
    static toCompositeVariantKey(components) {
        const result = {
            components: {},
        };
        for (const [key, pv] of Object.entries(components)) {
            const variant = new ConfiguratorPV(pv);
            if (variant.isNullish) {
                result.components[key] = null;
            }
            else {
                result.components[key] = {
                    product: {
                        productId: Number(variant.productId),
                        productKey: variant.productKey,
                    },
                    variant: {
                        variantId: Number(variant.variantId),
                        productId: Number(variant.productId),
                    },
                };
            }
        }
        return result;
    }
    static toVariantId(key, id) {
        return `${key}_${String(id)}`;
    }
}

export { ConfiguratorToDomainProductMapper, LogicEngineAdapter, LogicEngineBuilder, LogicEngineToConfiguratorValueMediator };
//# sourceMappingURL=bundle.mjs.map
